import * as THREE from 'three';
import { isInsideBrainVolume } from '../brain/createBrainGeometry';

const PARTICLE_COUNT = 600;

/**
 * The internal "neural activity" particle system: points drifting within the
 * brain volume, whose speed and color respond to the active substance's
 * simulated intensity.
 */
export class NeuralActivityParticles {
  readonly points: THREE.Points;
  readonly material: THREE.PointsMaterial;

  private readonly geometry: THREE.BufferGeometry;
  private readonly positions: Float32Array;
  private readonly velocities: Float32Array;
  private readonly basePositions: Float32Array;

  constructor(glowTexture: THREE.Texture) {
    this.positions = new Float32Array(PARTICLE_COUNT * 3);
    this.velocities = new Float32Array(PARTICLE_COUNT * 3);
    this.basePositions = new Float32Array(PARTICLE_COUNT * 3);

    for (let i = 0; i < PARTICLE_COUNT; i++) {
      const [x, y, z] = sampleInsideBrain();
      this.positions[i * 3] = x;
      this.positions[i * 3 + 1] = y;
      this.positions[i * 3 + 2] = z;
      this.basePositions[i * 3] = x;
      this.basePositions[i * 3 + 1] = y;
      this.basePositions[i * 3 + 2] = z;
      this.velocities[i * 3] = (Math.random() - 0.5) * 0.004;
      this.velocities[i * 3 + 1] = (Math.random() - 0.5) * 0.004;
      this.velocities[i * 3 + 2] = (Math.random() - 0.5) * 0.004;
    }

    this.geometry = new THREE.BufferGeometry();
    this.geometry.setAttribute('position', new THREE.BufferAttribute(this.positions, 3));

    this.material = new THREE.PointsMaterial({
      size: 0.04,
      map: glowTexture,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
      // The cortical surface is backed by an opaque shell, so these would
      // otherwise be hidden inside it. Drawing them without a depth test lets
      // internal activity glow through as if the shell were translucent.
      depthTest: false,
      transparent: true,
      opacity: 0.8,
      color: 0x44ccff,
    });

    this.points = new THREE.Points(this.geometry, this.material);
  }

  /** Advances particle motion. speedMultiplier scales drift/velocity (1.0 = baseline). */
  update(speedMultiplier: number): void {
    for (let i = 0; i < PARTICLE_COUNT; i++) {
      const idx = i * 3;

      this.velocities[idx] += (Math.random() - 0.5) * 0.0015 * speedMultiplier;
      this.velocities[idx + 1] += (Math.random() - 0.5) * 0.0015 * speedMultiplier;
      this.velocities[idx + 2] += (Math.random() - 0.5) * 0.0015 * speedMultiplier;

      this.velocities[idx] *= 0.96;
      this.velocities[idx + 1] *= 0.96;
      this.velocities[idx + 2] *= 0.96;

      const speed = Math.sqrt(
        this.velocities[idx] ** 2 + this.velocities[idx + 1] ** 2 + this.velocities[idx + 2] ** 2,
      );
      const maxSpeed = 0.018 * speedMultiplier;
      if (speed > maxSpeed) {
        const scale = maxSpeed / speed;
        this.velocities[idx] *= scale;
        this.velocities[idx + 1] *= scale;
        this.velocities[idx + 2] *= scale;
      }

      let newX = this.positions[idx] + this.velocities[idx];
      let newY = this.positions[idx + 1] + this.velocities[idx + 1];
      let newZ = this.positions[idx + 2] + this.velocities[idx + 2];

      if (!isInsideBrainVolume(newX, newY, newZ)) {
        this.velocities[idx] *= -0.7;
        this.velocities[idx + 1] *= -0.7;
        this.velocities[idx + 2] *= -0.7;
        newX = this.positions[idx] + this.velocities[idx];
        newY = this.positions[idx + 1] + this.velocities[idx + 1];
        newZ = this.positions[idx + 2] + this.velocities[idx + 2];

        if (!isInsideBrainVolume(newX, newY, newZ)) {
          newX = this.basePositions[idx];
          newY = this.basePositions[idx + 1];
          newZ = this.basePositions[idx + 2];
          this.velocities[idx] = (Math.random() - 0.5) * 0.006 * speedMultiplier;
          this.velocities[idx + 1] = (Math.random() - 0.5) * 0.006 * speedMultiplier;
          this.velocities[idx + 2] = (Math.random() - 0.5) * 0.006 * speedMultiplier;
        }
      }

      this.positions[idx] = newX;
      this.positions[idx + 1] = newY;
      this.positions[idx + 2] = newZ;
    }

    this.geometry.attributes.position.needsUpdate = true;
  }

  setColor(color: THREE.ColorRepresentation): void {
    this.material.color.set(color);
  }
}

function sampleInsideBrain(): [number, number, number] {
  for (let attempt = 0; attempt < 100; attempt++) {
    const r = 0.2 + Math.random() * 1.1;
    const theta = Math.random() * Math.PI;
    const phi = Math.random() * Math.PI * 2;
    const x = r * Math.sin(theta) * Math.cos(phi);
    const y = r * Math.cos(theta);
    const z = r * Math.sin(theta) * Math.sin(phi);
    if (isInsideBrainVolume(x, y, z)) return [x, y, z];
  }
  return [0, 0, 0];
}
